import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase'; // Import Firebase auth
import { onAuthStateChanged } from 'firebase/auth';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
} from 'firebase/auth';
import { FaCrown } from 'react-icons/fa';
import TaskCard from './TaskCard';
import ChallengeDetail from './ChallengeDetail';
import DemoBanner from './DemoBanner';
import { SignInModal, SignUpModal } from './Header'; // Import modals for sign-in and sign-up
import './TaskCard.css';

const TaskList = ({ language, difficulty, isPremium }) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('signIn'); // 'signIn' or 'signUp'
  const [isLoggedIn, setIsLoggedIn] = useState(false); // Track authentication state
  const [selectedTask, setSelectedTask] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');

  const allChallenges = {
    JavaScript: require('./challenges/javascriptChallenges.json'),
    'C++': require('./challenges/cppChallenges.json'),
    Python: require('./challenges/pythonChallenges.json'),
    Java: require('./challenges/javaChallenges.json'),
    PHP: require('./challenges/phpChallenges.json'),
    Prompt: require('./challenges/promptEng.json'),
    Machine:require('./challenges/machineL.json'),
    DataScience:require('./challenges/dataScience.json'),
  };

  const challenges = allChallenges[language] || [];
  const filteredTasks =
    difficulty === 'All' ? challenges : challenges.filter((task) => task.difficulty === difficulty);

  const visibleLimit = 4;
  const [visibleCount, setVisibleCount] = useState(visibleLimit);

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user); // Update login state
    });
    return () => unsubscribe();
  }, []);

  const handleOpenSignIn = () => {
    setShowModal(true);
    setActiveTab('signIn');
  };

  const handleOpenSignUp = () => {
    setShowModal(true);
    setActiveTab('signUp');
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      alert('User registered successfully!');
      setShowModal(false);
    } catch (error) {
      setError(error.message);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, email, password);
      alert('User signed in successfully!');
      setShowModal(false);
      navigate('/checkout'); // Navigate to checkout page after signing in
    } catch (error) {
      setError(error.message);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      alert('Signed in with Google!');
      setShowModal(false);
      navigate('/checkout'); // Navigate to checkout page after signing in
    } catch (error) {
      setError(error.message);
    }
  };

  const handleNavigateToCheckout = () => {
    if (isLoggedIn) {
      navigate('/checkout'); // Go to checkout if logged in
    } else {
      handleOpenSignIn(); // Open sign-in modal if not logged in
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
  };

  const handleSolve = (task) => {
    setSelectedTask(task);
  };

  const handleCloseTask = () => {
    setSelectedTask(null);
  };

  const loadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 4, filteredTasks.length));
  };

  return (
    <div className="task-list">
      <DemoBanner />
      {selectedTask ? (
        <ChallengeDetail task={selectedTask} onClose={handleCloseTask} />
      ) : (
        <>
          {filteredTasks.slice(0, visibleCount).map((task, index) => (
  <React.Fragment key={index}>
    <TaskCard task={task} onSolve={handleSolve} />
    {!isPremium && index === visibleLimit - 1 && (
      <div className="buy-premium-container">
        <button className="buy-premium" onClick={handleNavigateToCheckout}>
          <FaCrown /> Buy Premium to Unlock All Challenges
        </button>
        {!isLoggedIn && (
      <div className="sign-up-section" style={{ display: 'none' }}>
        <button className="sign-up-btn" onClick={handleOpenSignUp}>
          Sign Up
        </button>
      </div>
    )} 
      </div>
    )}
  </React.Fragment>
))}

{!isPremium && filteredTasks.length > visibleLimit && (
  <div className="task-card blurred">
    <div className="blurred-content">
      {filteredTasks.slice(visibleLimit).map((task, index) => (
        <TaskCard key={index} task={task} onSolve={handleSolve} />
      ))}
    </div>
  </div>
)}

          {isPremium && visibleCount < filteredTasks.length && (
            <button className="load-more" onClick={loadMore}>
              Load More
            </button>
          )}
        </>
      )}

      {/* Render Sign-In/Sign-Up Modal */}
      {showModal && (
        <div className="modal">
          {activeTab === 'signIn' ? (
            <SignInModal
              closeModal={handleCloseModal}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              handleSignIn={handleSignIn}
              handleGoogleSignIn={handleGoogleSignIn}
              setActiveTab={setActiveTab}
              error={error}
            />
          ) : (
            <SignUpModal
              closeModal={handleCloseModal}
              username={username}
              setUsername={setUsername}
              email={email}
              setEmail={setEmail}
              password={password}
              setPassword={setPassword}
              handleSignUp={handleSignUp}
              handleGoogleSignIn={handleGoogleSignIn}
              setActiveTab={setActiveTab}
              error={error}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default TaskList;
